import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Card, CardProps, Center, Flex, Group, MenuItemProps, Stack, Text } from "@mantine/core";
import { Product } from "src/graphql/generated/graphql";

import { FormItem } from "../FormItem";
import { Link } from "../Link";

export interface CampaignProductCardOption extends MenuItemProps {
    to?: string;
}

export interface CampaignProductCardProps extends Omit<CardProps, "children"> {
    product: Product;
    actions?: ReactNode;
    bottomSection?: ReactNode;
}

export const CampaignProductCard = ({ product, actions, bottomSection, ...rest }: CampaignProductCardProps) => {
    const { t } = useTranslation(["common"]);

    return (
        <Card p="md" shadow="sm" radius="md" withBorder {...rest}>
            <Card.Section>
                <Flex align="stretch">
                    <Box w="auto">
                        {product?.thumbnailImage?.src ? (
                            <Avatar
                                src={`${product?.thumbnailImage?.src}?w=120`}
                                radius="md"
                                size="112px"
                                style={{
                                    borderTopRightRadius: 0,
                                    borderBottomRightRadius: 0,
                                    borderBottomLeftRadius: 0,
                                }}
                            />
                        ) : (
                            <Avatar radius="md" size="112px">
                                {String(product?.name)
                                    .split(" ")
                                    .map((w) => w.toUpperCase()[0])
                                    .join("")}
                            </Avatar>
                        )}
                    </Box>
                    <Center ml="xl" style={{ flex: 1, justifyContent: "flex-start" }}>
                        <Stack spacing="xs">
                            <Text weight={500}>{product?.name}</Text>
                            <Group spacing="xl">
                                <FormItem label={t("product_manufacturer")}>
                                    {product?.info?.manufacturer?.id ? (
                                        <Link to={`/products/manufacturer/${product.info.manufacturer.id}`}>
                                            {product.info.manufacturer.name}
                                        </Link>
                                    ) : (
                                        <Text color="dimmed">-</Text>
                                    )}
                                </FormItem>
                                <FormItem label={t("product_model")}>
                                    {product?.info?.model || <Text color="dimmed">-</Text>}
                                </FormItem>
                                <FormItem label={t("product_code_ean")}>
                                    {product?.codes?.ean || <Text color="dimmed">-</Text>}
                                </FormItem>
                            </Group>
                        </Stack>
                    </Center>
                    {actions && <Box m="md">{actions}</Box>}
                </Flex>
            </Card.Section>
            {bottomSection && (
                <Card.Section py="md" inheritPadding withBorder>
                    {bottomSection}
                </Card.Section>
            )}
        </Card>
    );
};
